import { Box } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  IStageStatusTurnAroundTime,
  getSubmissionsTurnAroundTimeAndCandidateByStatus,
} from 'wisestep-api';
import { WsTypography, getDateUsingFormat } from 'wisestep-base';

import { stageStatusPipelineType } from '../../../../features/crmSettings/customizeStageStatus/helper';
import { useJob } from '../../../../features/jobs/hooks';
import { getStageStatusSettings } from '../../../../utils/CandidateStageStatusHelper';
import DefaultStageStatusPipeline from '../../../../utils/DefaultStageStatusPipeline';

interface StageData {
  timeStamp: string;
  timeDifference: number;
}
interface IAggregatedData {
  candidateName?: string;
  candidateEmail?: string;
  currentStage?: string;
  currentStageTimeStamp?: string;
  timeToSubmit?: string;
  stageMap?: Record<string, StageData>;
}

const TurnAroundTimeByStagesTable = ({ jobId }: { jobId: string }) => {
  const paginationSize = 10;
  const tableData: Record<string, unknown>[] = [];
  const stageArr: string[] = [];
  const timeToSubmitArr: Record<string, any> = {};
  const uniqueCandidates: IAggregatedData[] = [];
  const [fetchedData, setFetchedData] = useState<IStageStatusTurnAroundTime[]>([]);
  const [stages, setStages] = useState<stageStatusPipelineType[]>([]);
  const [job] = useJob(jobId);
  const postedDate = job?.createdOn ?? '';
  useEffect(() => {
    const fetchData = async () => {
      const response = await getSubmissionsTurnAroundTimeAndCandidateByStatus(jobId);
      const stageStatusResponse = await getStageStatusSettings();
      const parsedStages = JSON.parse(
        stageStatusResponse?.data?.stageStatusPipeline ?? DefaultStageStatusPipeline
      );
      setStages(parsedStages);
      setFetchedData(response.data);
    };
    fetchData();
  }, [jobId]);

  fetchedData.forEach((item) => {
    const candidateName = item.candidateName ?? '';
    const candidateEmail = item.candidateEmail ?? '';
    const currentStage = item.currentStage ?? '';
    const timeToSubmit = item.stageEntryTimeStamp?.toString();
    const currentStageTimeStamp =
      item.currentStageTimeStamp &&
      getDateUsingFormat(item.currentStageTimeStamp.toString(), 'DD-MMM | HH:mm');
    const timeStamp =
      getDateUsingFormat(item.stageEntryTimeStamp.toString(), 'DD-MMM | HH:mm') ?? 'N/A';
    const timeDifference = item.avgStageDurationSeconds ?? 0;
    const stage = item.applicationStage ?? '';
    const existCandidateData = uniqueCandidates.find(
      (c) => c.candidateName == candidateName && c.candidateEmail == candidateEmail
    );
    if (existCandidateData) {
      if (existCandidateData.stageMap && existCandidateData.stageMap[stage]) {
        existCandidateData.stageMap[stage].timeDifference += timeDifference;
        existCandidateData.stageMap[stage].timeStamp = timeStamp;
      } else {
        existCandidateData.stageMap = {
          ...existCandidateData.stageMap,
          [stage]: { timeStamp, timeDifference },
        };
      }
    } else {
      const candidateData: IAggregatedData = {
        candidateName,
        candidateEmail,
        currentStage,
        currentStageTimeStamp,
        timeToSubmit,
        stageMap: { [stage]: { timeStamp, timeDifference } },
      };
      uniqueCandidates.push(candidateData);
    }
  });
  const result: IAggregatedData[] = uniqueCandidates;

  result?.forEach((item) => {
    const stages = new Set(Object.keys(item?.stageMap ?? {}));
    const timeStampsArray = Array.from(stages).map((stage) => {
      if (item?.stageMap && item.stageMap[stage].timeStamp) {
        const date1 = new Date(job?.createdOn ?? '');
        const date2 = new Date(item.timeToSubmit ?? '');
        const timeDifferenceDays = Math.floor(
          (date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24)
        );
        return timeDifferenceDays + 'd';
      }
    });
    const candidateName = item?.candidateName ?? '';
    const stagesArray = Array.from(stages);
    for (let i = 0; i < stagesArray.length; i++) {
      stageArr.push(stagesArray[i]);
      if (i === 0) timeToSubmitArr[candidateName] = timeStampsArray[i];
    }
  });
  const uniqueStagesSet = new Set(stageArr);

  const stageOrderMap = new Map(stages.map((stage) => [stage.stage, stage.stageOrder]));
  const stagesSorted = Array.from(uniqueStagesSet).sort((item1, item2) => {
    const stageOrderA = stageOrderMap.get(item1) || 0;
    const stageOrderB = stageOrderMap.get(item2) || 0;
    return stageOrderA - stageOrderB;
  });

  result?.forEach((item) => {
    const candidateName = item.candidateName ?? '';
    const rowData = {
      id: uuidv4(),
      candidateName: item.candidateName ?? '',
      candidateEmail: item.candidateEmail ?? '',
      currentStage: item.currentStage ?? '',
      currentStageTimeStamp: item.currentStageTimeStamp ?? '',
      timeToSubmit: timeToSubmitArr[candidateName] ?? '',
    };
    Object.entries(item?.stageMap ?? {}).forEach((entry) => {
      const roundedTimeDifference = Math.round(entry[1].timeDifference);
      rowData[entry[0]] = `${entry[1].timeStamp} (${roundedTimeDifference}d)`;
    });
    tableData.push(rowData);
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'id', hide: true },
    {
      field: 'candidateName',
      headerName: `Candidate Name`,
      width: 180,
    },
    {
      field: 'candidateEmail',
      headerName: `Candidate Email`,
      width: 180,
    },
    {
      field: 'timeToSubmit',
      headerName: `Time to Submit`,
      width: 180,
    },
    ...stagesSorted.map((stage) => ({
      field: stage,
      headerName: stage,
      width: 200,
      renderCell: (field) => {
        if (field.value === null || typeof field.value === 'undefined') {
          return '--';
        }
        return field.value;
      },
    })),
    {
      field: 'currentStage',
      headerName: `Current Stage`,
      width: 180,
    },
    {
      field: 'currentStageTimeStamp',
      headerName: `Current Stage TimeStamp`,
      width: 200,
    },
  ];

  return (
    <Box height="100%" width="100%">
      <WsTypography bold={true}>
        {`Turn Around Time across stages for ${job?.title} (Posted Date: ${getDateUsingFormat(
          postedDate.toString(),
          'DD-MMM-YYYY'
        )})`}
      </WsTypography>
      <Box mt={1.5} style={{ height: 680 }}>
        <DataGrid
          columns={[...columns]}
          rows={tableData}
          components={{ Toolbar: GridToolbar }}
          pageSize={paginationSize}
          rowsPerPageOptions={[paginationSize]}
        />
      </Box>
    </Box>
  );
};

export default TurnAroundTimeByStagesTable;
